import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
    Users,
    Package,
    CurrencyInr,
    Clock,
    CheckCircle,
    ChatTeardropDots,
    ChartLine,
    Tag,
} from "@phosphor-icons/react";

function Stat({ icon: Icon, label, value, g }) {
    return (
        <div
            className={`relative overflow-hidden ${g} p-5 text-white shadow-lg`}
        >
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full blur-2xl" />
            <div className="flex items-center justify-between relative">
                <div className="text-xs uppercase tracking-widest text-white/80">
                    {label}
                </div>
                <Icon size={20} weight="duotone" className="text-white/90" />
            </div>
            <div className="font-display font-black text-4xl tracking-tighter mt-2 relative">
                {value}
            </div>
        </div>
    );
}

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [vis, setVis] = useState(null);

    useEffect(() => {
        api.get("/admin/stats")
            .then(({ data }) => setStats(data))
            .catch(() => {});
        api.get("/admin/visitors")
            .then(({ data }) => setVis(data))
            .catch(() => {});
    }, []);

    return (
        <div
            className="max-w-7xl mx-auto px-6 py-10"
            data-testid="admin-dashboard"
        >
            {/* Gradient welcome */}
            <div className="relative overflow-hidden mb-8 p-8 bg-mesh-dark text-white shadow-2xl">
                <div className="absolute top-0 right-0 w-96 h-96 g-pink opacity-30 rounded-full blur-3xl" />
                <div className="relative flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-widest text-violet-300">
                            Admin · Control Center
                        </div>
                        <h1 className="font-display font-black text-5xl tracking-tighter mt-1">
                            Command{" "}
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-400 via-pink-400 to-amber-400">
                                everything.
                            </span>
                        </h1>
                        <p className="text-white/70 text-sm mt-2 max-w-md">
                            Packages, payments, users — all at a glance.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Link
                            to="/admin/payments"
                            className="bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 px-4 py-2 text-sm flex items-center gap-2"
                        >
                            <Clock size={14} /> {stats?.pending_payments ?? 0}{" "}
                            Pending
                        </Link>
                    </div>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Stat
                    icon={Users}
                    label="Users"
                    value={stats?.total_users ?? "—"}
                    g="g-violet"
                />
                <Stat
                    icon={Clock}
                    label="Pending Payments"
                    value={stats?.pending_payments ?? "—"}
                    g="g-sunset"
                />
                <Stat
                    icon={CheckCircle}
                    label="Approved"
                    value={stats?.approved_payments ?? "—"}
                    g="g-emerald"
                />
                <Stat
                    icon={CurrencyInr}
                    label="Revenue"
                    value={`₹${(stats?.revenue || 0).toFixed(0)}`}
                    g="g-ocean"
                />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
                <Link
                    to="/admin/packages"
                    className="card-surface p-6 hover:-translate-y-1 hover:shadow-xl group relative overflow-hidden"
                    data-testid="link-admin-packages"
                >
                    <div className="absolute top-0 left-0 right-0 h-1 g-violet" />
                    <div className="w-12 h-12 g-violet text-white flex items-center justify-center mb-3 shadow-lg">
                        <Package size={24} weight="duotone" />
                    </div>
                    <div className="font-display font-bold text-lg">
                        Packages
                    </div>
                    <div className="text-sm text-zinc-500">
                        Create, edit, disable card packages.
                    </div>
                </Link>
                <Link
                    to="/admin/coupons"
                    className="card-surface p-6 hover:-translate-y-1 hover:shadow-xl group relative overflow-hidden"
                    data-testid="link-admin-coupons"
                >
                    <div className="absolute top-0 left-0 right-0 h-1 g-sunset" />
                    <div className="w-12 h-12 g-sunset text-white flex items-center justify-center mb-3 shadow-lg">
                        <Tag size={24} weight="duotone" />
                    </div>
                    <div className="font-display font-bold text-lg">Offers</div>
                    <div className="text-sm text-zinc-500">
                        Coupon codes &amp; sale pricing.
                    </div>
                </Link>
                <Link
                    to="/admin/payments"
                    className="card-surface p-6 hover:-translate-y-1 hover:shadow-xl group relative overflow-hidden"
                    data-testid="link-admin-payments"
                >
                    <div className="absolute top-0 left-0 right-0 h-1 g-sunset" />
                    <div className="w-12 h-12 g-sunset text-white flex items-center justify-center mb-3 shadow-lg">
                        <CurrencyInr size={24} weight="duotone" />
                    </div>
                    <div className="font-display font-bold text-lg">
                        Payments
                    </div>
                    <div className="text-sm text-zinc-500">
                        Approve or reject user payments.
                    </div>
                </Link>
                <Link
                    to="/admin/users"
                    className="card-surface p-6 hover:-translate-y-1 hover:shadow-xl group relative overflow-hidden"
                    data-testid="link-admin-users"
                >
                    <div className="absolute top-0 left-0 right-0 h-1 g-emerald" />
                    <div className="w-12 h-12 g-emerald text-white flex items-center justify-center mb-3 shadow-lg">
                        <Users size={24} weight="duotone" />
                    </div>
                    <div className="font-display font-bold text-lg">Users</div>
                    <div className="text-sm text-zinc-500">
                        Verify accounts, adjust credits.
                    </div>
                </Link>
                <Link
                    to="/admin/leads"
                    className="card-surface p-6 hover:-translate-y-1 hover:shadow-xl group relative overflow-hidden"
                    data-testid="link-admin-leads"
                >
                    <div className="absolute top-0 left-0 right-0 h-1 g-ocean" />
                    <div className="w-12 h-12 g-ocean text-white flex items-center justify-center mb-3 shadow-lg">
                        <ChatTeardropDots size={24} weight="duotone" />
                    </div>
                    <div className="font-display font-bold text-lg">Leads</div>
                    <div className="text-sm text-zinc-500">
                        Demo requests & WhatsApp clicks.
                    </div>
                </Link>
                <Link
                    to="/admin/visitors"
                    className="card-surface p-6 hover:-translate-y-1 hover:shadow-xl group relative overflow-hidden"
                    data-testid="link-admin-visitors"
                >
                    <div className="absolute top-0 left-0 right-0 h-1 g-violet" />
                    <div className="w-12 h-12 g-violet text-white flex items-center justify-center mb-3 shadow-lg">
                        <ChartLine size={24} weight="duotone" />
                    </div>
                    <div className="font-display font-bold text-lg flex items-center gap-2">
                        Visitor Analytics
                        {vis?.live_now > 0 && (
                            <span className="text-[10px] font-mono bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm">
                                {vis.live_now} live
                            </span>
                        )}
                    </div>
                    <div className="text-sm text-zinc-500">
                        {vis?.total_visits ?? 0} total ·{" "}
                        {vis?.unique_visitors ?? 0} unique visitors.
                    </div>
                </Link>
            </div>
        </div>
    );
}
